import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { TextField } from '@material-ui/core';

/**
 * Keyword cell of a Terminology entry on the grid's parent row.
 *
 * Double-click turns the keyword into an input; blur saves (same behaviour as the
 * translation cells), Esc cancels. An empty or unchanged value is a no-op. When the
 * save fails (e.g. duplicate keyword) the input stays open with the error under it.
 * The keyword itself is data - never wrapped with tr().
 */
export default function TerminologyKeywordCell({ keyword, isEditable, submitting, onSave, renderError }) {
    const [isEditing, setIsEditing] = useState(false);
    const [text, setText] = useState('');
    const [errorCode, setErrorCode] = useState('');
    // Set on Esc so the blur it triggers cancels instead of saving.
    const cancelRef = useRef(false);

    const close = () => {
        setIsEditing(false);
        setErrorCode('');
    };

    const handleSave = async () => {
        const value = text.trim();
        if (!value || value === keyword) {
            close();
            return;
        }
        const resultError = await onSave(value);
        if (resultError) {
            setErrorCode(resultError);
            return;
        }
        close();
    };

    if (!isEditing) {
        // Long keyword: one line cut with "..." (.tm-ellipsis-text). Full text shows when editing.
        if (!isEditable) {
            return (
                <td className="tm-src-text tm-ellipsis-cell">
                    <span className="tm-ellipsis-text">{keyword}</span>
                </td>
            );
        }
        return (
            <td className="tm-src-text tm-ellipsis-cell">
                {/* Plain text, no hover effect; double-click edits (same as the detail rows). */}
                <span
                    className="tm-ellipsis-text"
                    onDoubleClick={e => {
                        e.stopPropagation();
                        if (submitting) return;
                        setText(keyword || '');
                        setIsEditing(true);
                    }}
                >
                    {keyword}
                </span>
            </td>
        );
    }

    return (
        <td className="tm-src-text" onClick={e => e.stopPropagation()}>
            <TextField
                className="tm-text-input"
                variant="outlined"
                size="small"
                fullWidth
                autoFocus
                value={text}
                error={Boolean(errorCode)}
                disabled={submitting}
                onChange={e => {
                    setText(e.target.value);
                    setErrorCode('');
                }}
                onBlur={() => {
                    if (cancelRef.current) {
                        cancelRef.current = false;
                        close();
                        return;
                    }
                    handleSave();
                }}
                onKeyDown={e => {
                    if (e.key === 'Escape') {
                        cancelRef.current = true;
                        e.target.blur();
                    } else if (e.key === 'Enter') {
                        e.preventDefault();
                        e.target.blur();
                    }
                }}
            />
            {errorCode && <div className="tm-new-row-error">{renderError(errorCode)}</div>}
        </td>
    );
}

TerminologyKeywordCell.propTypes = {
    keyword: PropTypes.string,
    isEditable: PropTypes.bool,
    submitting: PropTypes.bool,
    // (newKeyword) => Promise<string>: '' on success, a NEW_ROW_ERROR code otherwise.
    onSave: PropTypes.func.isRequired,
    renderError: PropTypes.func.isRequired
};

TerminologyKeywordCell.defaultProps = {
    keyword: '',
    isEditable: false,
    submitting: false
};
